import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Body1,
  Button,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  SearchBox,
  Select,
  Skeleton,
  SkeletonItem,
  TableCellLayout,
  Text,
  Title2,
  Tooltip,
  createTableColumn,
  type TableColumnDefinition,
  type TableColumnSizingOptions,
} from '@fluentui/react-components';
import { AddRegular, DeleteRegular, EditRegular } from '@fluentui/react-icons';
import { useMsal } from '@azure/msal-react';
import { useApiRequest } from '../hooks/useApiRequest';
import { ApiService } from '../services/apiService';
import { getFriendlyApiErrorMessage, logApiError } from '../services/apiErrorMessages';
import { useAlertBannersPageStyles } from '../styles/AlertBannersPage.styles';
import { SEARCH_DEBOUNCE_MS } from '../constants/table';
import { TIER_OPTIONS, type Tier } from '../constants/tiers';
import {
  ALERT_TITLES,
  SEVERITY_OPTIONS,
} from '../constants/alerts';
import {
  getAlertStatus,
  type Alert,
  type AlertSeverity,
  type AlertStatus,
  type AlertsResponse,
} from '../models/alert';
import { renderAlertMessage } from '../utils/alertMessages';
import { formatPacificLongDate } from '../utils/formatters';
import { SummaryCardBar, SUMMARY_CARD_COLORS } from '../components/SummaryCardBar';
import { AlertBannerFormFlyout } from '../components/AlertBannerFormFlyout';

// ────────────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────────────

// Column widths — tightened for identifier/status columns; Message is intentionally
// the widest since its content dominates row length.
const COLUMN_SIZING: TableColumnSizingOptions = {
  status:     { minWidth: 90,  idealWidth: 100 },
  type:       { minWidth: 90,  idealWidth: 100 },
  tier:       { minWidth: 90,  idealWidth: 100 },
  locationId: { minWidth: 120, idealWidth: 140 },
  title:      { minWidth: 160, idealWidth: 200 },
  message:    { minWidth: 500, idealWidth: 600 },
  startDate:  { minWidth: 140, idealWidth: 160 },
  endDate:    { minWidth: 140, idealWidth: 160 },
  actions:    { minWidth: 90,  idealWidth: 90 },
};

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

const joinOrAll = (list: string[] | null | undefined, allLabel: string): string =>
  !list || list.length === 0 ? allLabel : list.join(', ');

/**
 * Tier-column formatter that distinguishes the two "no listed tier" cases:
 *   - `null`  → the alert has no tier restriction (every partner)
 *   - `[]`    → the alert explicitly targets no tier (Location IDs only)
 */
const formatTierList = (list: string[] | null | undefined): string => {
  if (list == null) return 'All partners';
  if (list.length === 0) return 'None';
  return list.join(', ');
};

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

type StatusFilter = AlertStatus | 'All';
type SeverityFilter = AlertSeverity | 'All';
type AudienceFilter = 'All' | 'AllPartners' | Tier;

export const AlertBannersPage: React.FC = () => {
  const styles = useAlertBannersPageStyles();
  const { instance, accounts } = useMsal();
  const { data, isLoading, error, execute } = useApiRequest<AlertsResponse>();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('All');
  const [audienceFilter, setAudienceFilter] = useState<AudienceFilter>('All');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [alertPendingDelete, setAlertPendingDelete] = useState<Alert | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isAddFlyoutOpen, setIsAddFlyoutOpen] = useState(false);
  const [alertToEdit, setAlertToEdit] = useState<Alert | null>(null);

  const fetchAlerts = useCallback(
    () => execute((api) => api.get<AlertsResponse>('/Alerts')),
    [execute],
  );

  // Debounce the search input so each keystroke doesn't rescan the list.
  useEffect(() => {
    const handle = setTimeout(() => setSearchQuery(searchInput.trim().toLowerCase()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    void fetchAlerts();
  }, [fetchAlerts]);

  const alerts = useMemo(() => data?.alerts ?? [], [data]);

  const statusCounts = useMemo(() => {
    const now = Date.now();
    const counts = { Active: 0, Scheduled: 0, Inactive: 0 } as Record<AlertStatus, number>;
    for (const alert of alerts) counts[getAlertStatus(alert, now)] += 1;
    return counts;
  }, [alerts]);

  const summaryCards = useMemo(
    () => [
      { label: 'Active',    value: statusCounts.Active,    accentColor: SUMMARY_CARD_COLORS.dataVizSlot1, filterValue: 'Active' },
      { label: 'Scheduled', value: statusCounts.Scheduled, accentColor: SUMMARY_CARD_COLORS.dataVizSlot2, filterValue: 'Scheduled' },
      { label: 'Inactive',  value: statusCounts.Inactive,  accentColor: SUMMARY_CARD_COLORS.dataVizSlot3, filterValue: 'Inactive' },
    ],
    [statusCounts],
  );

  const onStatusCardClick = useCallback(
    (value: string) => {
      // Clicking the currently-active card clears the filter (behaves like a toggle).
      setStatusFilter((prev) => (prev === value ? 'All' : (value as StatusFilter)));
    },
    [],
  );

  const filteredAlerts = useMemo(() => {
    const now = Date.now();
    return alerts.filter((alert) => {
      const status = getAlertStatus(alert, now);
      if (statusFilter !== 'All' && status !== statusFilter) return false;
      if (severityFilter !== 'All' && alert.severity !== severityFilter) return false;

      if (audienceFilter !== 'All') {
        const tiers = alert.targetTiers ?? [];
        if (audienceFilter === 'AllPartners') {
          if (tiers.length > 0) return false;
        } else if (!tiers.includes(audienceFilter)) {
          return false;
        }
      }

      if (searchQuery) {
        const title = ALERT_TITLES[alert.messageKey].toLowerCase();
        const message = renderAlertMessage(alert).toLowerCase();
        const tiersText = (alert.targetTiers ?? []).join(' ').toLowerCase();
        const locationsText = (alert.targetLocationIds ?? []).join(' ').toLowerCase();
        const createdBy = alert.createdBy.toLowerCase();
        if (
          !title.includes(searchQuery) &&
          !message.includes(searchQuery) &&
          !tiersText.includes(searchQuery) &&
          !locationsText.includes(searchQuery) &&
          !createdBy.includes(searchQuery)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [alerts, statusFilter, severityFilter, audienceFilter, searchQuery]);

  const handleAddBanner = useCallback(() => {
    setAlertToEdit(null);
    setIsAddFlyoutOpen(true);
  }, []);

  const handleCloseAddFlyout = useCallback(() => {
    setIsAddFlyoutOpen(false);
    setAlertToEdit(null);
  }, []);

  const handleEdit = useCallback((alert: Alert) => {
    setAlertToEdit(alert);
    setIsAddFlyoutOpen(true);
  }, []);

  const handleDelete = useCallback((alert: Alert) => {
    setDeleteError(null);
    setAlertPendingDelete(alert);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!alertPendingDelete) return;

    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) {
      setDeleteError('No authenticated account found.');
      return;
    }

    const id = alertPendingDelete.id;
    setDeletingId(id);
    setDeleteError(null);

    try {
      const api = new ApiService(instance, account);
      await api.delete<void>(`/Alerts/${encodeURIComponent(id)}`);
      setAlertPendingDelete(null);
      await fetchAlerts();
    } catch (err) {
      logApiError('AlertBannersPage.confirmDelete', err);
      setDeleteError(getFriendlyApiErrorMessage(err, 'Failed to delete the alert.'));
    } finally {
      setDeletingId(null);
    }
  }, [alertPendingDelete, instance, accounts, fetchAlerts]);

  const columns: TableColumnDefinition<Alert>[] = useMemo(
    () => [
      createTableColumn<Alert>({
        columnId: 'status',
        renderHeaderCell: () => 'Status',
        renderCell: (alert) => <TableCellLayout>{getAlertStatus(alert)}</TableCellLayout>,
      }),
      createTableColumn<Alert>({
        columnId: 'type',
        renderHeaderCell: () => 'Type',
        renderCell: (alert) => <TableCellLayout>{alert.severity}</TableCellLayout>,
      }),
      createTableColumn<Alert>({
        columnId: 'tier',
        renderHeaderCell: () => 'Tier',
        renderCell: (alert) => (
          <TableCellLayout>{formatTierList(alert.targetTiers)}</TableCellLayout>
        ),
      }),
      createTableColumn<Alert>({
        columnId: 'locationId',
        renderHeaderCell: () => 'Location ID',
        renderCell: (alert) => (
          <TableCellLayout>{joinOrAll(alert.targetLocationIds, 'All locations')}</TableCellLayout>
        ),
      }),
      createTableColumn<Alert>({
        columnId: 'title',
        renderHeaderCell: () => 'Title',
        renderCell: (alert) => <TableCellLayout>{ALERT_TITLES[alert.messageKey]}</TableCellLayout>,
      }),
      createTableColumn<Alert>({
        columnId: 'message',
        renderHeaderCell: () => 'Message',
        renderCell: (alert) => (
          <TableCellLayout>
            <span className={styles.messageCell}>{renderAlertMessage(alert)}</span>
          </TableCellLayout>
        ),
      }),
      createTableColumn<Alert>({
        columnId: 'startDate',
        renderHeaderCell: () => 'Start date',
        renderCell: (alert) => <TableCellLayout>{formatPacificLongDate(alert.startTimeUtc)}</TableCellLayout>,
      }),
      createTableColumn<Alert>({
        columnId: 'endDate',
        renderHeaderCell: () => 'End date',
        renderCell: (alert) => <TableCellLayout>{formatPacificLongDate(alert.endTimeUtc)}</TableCellLayout>,
      }),
      createTableColumn<Alert>({
        columnId: 'actions',
        renderHeaderCell: () => '',
        renderCell: (alert) => (
          <TableCellLayout>
            <div className={styles.actions}>
              <Tooltip content="Edit alert" relationship="label">
                <Button
                  appearance="outline"
                  size="small"
                  icon={<EditRegular />}
                  onClick={() => handleEdit(alert)}
                  aria-label="Edit alert"
                  className={styles.iconButton}
                />
              </Tooltip>
              <Tooltip content="Delete alert" relationship="label">
                <Button
                  appearance="outline"
                  size="small"
                  icon={<DeleteRegular />}
                  onClick={() => handleDelete(alert)}
                  aria-label="Delete alert"
                  className={styles.iconButton}
                />
              </Tooltip>
            </div>
          </TableCellLayout>
        ),
      }),
    ],
    [styles, handleEdit, handleDelete],
  );

  return (
    <div className={styles.pageRoot}>
      <div className={styles.header}>
        <Title2>Alert banners</Title2>
      </div>

      <SummaryCardBar
        cards={summaryCards}
        isLoading={isLoading && alerts.length === 0}
        activeFilter={statusFilter === 'All' ? undefined : statusFilter}
        onCardClick={onStatusCardClick}
      />

      {error && (
        <div className={styles.errorBar}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Failed to load alerts</MessageBarTitle>
              {error}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      <div className={styles.addBannerRow}>
        <Button appearance="primary" icon={<AddRegular />} onClick={handleAddBanner}>
          Add banner
        </Button>
      </div>

      <hr className={styles.divider} />

      <div className={styles.filtersRow}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Search</label>
          <SearchBox
            placeholder="Title, message, tier, location, author…"
            value={searchInput}
            onChange={(_e, d) => setSearchInput(d.value)}
            style={{ minWidth: '280px' }}
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="filterStatus">Status</label>
          <Select
            id="filterStatus"
            value={statusFilter}
            onChange={(_e, d) => setStatusFilter(d.value as StatusFilter)}
          >
            <option value="All">All</option>
            <option value="Active">Active</option>
            <option value="Scheduled">Scheduled</option>
            <option value="Inactive">Inactive</option>
          </Select>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="filterType">Type</label>
          <Select
            id="filterType"
            value={severityFilter}
            onChange={(_e, d) => setSeverityFilter(d.value as SeverityFilter)}
          >
            <option value="All">All</option>
            {SEVERITY_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </Select>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="filterAudience">Audience</label>
          <Select
            id="filterAudience"
            value={audienceFilter}
            onChange={(_e, d) => setAudienceFilter(d.value as AudienceFilter)}
          >
            <option value="All">All</option>
            <option value="AllPartners">All partners (no target)</option>
            {TIER_OPTIONS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </Select>
        </div>
      </div>

      {!(isLoading && alerts.length === 0) && (
        <Text size={200} block className={styles.resultCount}>
          Showing {filteredAlerts.length === 0 ? 0 : 1} - {filteredAlerts.length} of {alerts.length}
        </Text>
      )}

      <div className={styles.tableWrapper}>
        {isLoading && alerts.length === 0 ? (
          <Skeleton>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
              {Array.from({ length: 9 }, (_, i) => (
                <SkeletonItem key={`h-${i}`} style={{ width: `${80 + (i % 3) * 20}px`, height: '14px' }} />
              ))}
            </div>
            {Array.from({ length: 8 }, (_, row) => (
              <div key={`r-${row}`} style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                {Array.from({ length: 9 }, (_, col) => (
                  <SkeletonItem key={`c-${col}`} style={{ width: `${80 + (col % 3) * 20}px`, height: '14px' }} />
                ))}
              </div>
            ))}
          </Skeleton>
        ) : filteredAlerts.length === 0 ? (
          <div className={styles.emptyState}>
            <Body1>{alerts.length === 0 ? 'No alerts have been created yet.' : 'No alerts match the current filters.'}</Body1>
          </div>
        ) : (
          <DataGrid
            items={filteredAlerts}
            columns={columns}
            getRowId={(a) => a.id}
            size="medium"
            resizableColumns
            resizableColumnsOptions={{ autoFitColumns: false }}
            columnSizingOptions={COLUMN_SIZING}
          >
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<Alert>>
              {({ item, rowId }) => (
                <DataGridRow<Alert> key={rowId}>
                  {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
        )}
      </div>

      <AlertBannerFormFlyout
        open={isAddFlyoutOpen}
        onClose={handleCloseAddFlyout}
        alertToEdit={alertToEdit}
        onSaved={fetchAlerts}
      />

      <Dialog
        open={alertPendingDelete !== null}
        onOpenChange={(_e, data) => {
          if (!data.open && deletingId === null) {
            setAlertPendingDelete(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete Message</DialogTitle>
            <DialogContent>
              Are you sure you want to delete the message?
              <br />
              This action can't be undone.
              {deleteError && (
                <MessageBar intent="error" style={{ marginTop: '12px' }}>
                  <MessageBarBody>{deleteError}</MessageBarBody>
                </MessageBar>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={() => void confirmDelete()}
                disabled={deletingId !== null}
              >
                {deletingId !== null ? 'Deleting…' : 'Delete'}
              </Button>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary" disabled={deletingId !== null}>
                  Cancel
                </Button>
              </DialogTrigger>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
